import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_SITEKEY, FORMSPREE_URL, getWhatsAppUrl } from '../config/constants';
import usePageTitle from '../hooks/usePageTitle';
import './Contact.css';

function Contact() {
  usePageTitle('Contáctanos');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [captcha, setCaptcha] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const captchaRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCaptcha = (value) => {
    setCaptcha(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar captcha
    if (!captcha) {
      alert('Por favor, completa el captcha para verificar que no eres un robot.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Crear FormData para enviar a Formspree
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('g-recaptcha-response', captcha);

      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        body: formDataToSend,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Limpiar formulario
        setFormData({ name: '', email: '', phone: '', message: '' });
        setCaptcha(null);
        captchaRef.current.reset();

        // También enviar por WhatsApp como backup
        const whatsappMessage = `¡Nuevo contacto desde la web!\n\n📧 Nombre: ${formData.name}\n📧 Email: ${formData.email}\n📱 Teléfono: ${formData.phone}\n💬 Mensaje: ${formData.message}`;

        setTimeout(() => {
          window.open(getWhatsAppUrl(whatsappMessage), '_blank');
        }, 2000);
      } else {
        throw new Error('Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="contact-hero-content">
          <h1>¡Contáctanos!</h1>
          <p>
            ¿Listo para hacer de tu evento algo inolvidable? Escríbenos y te
            responderemos súper rápido.
          </p>
        </div>
      </div>

      <div className="container">
        <div className="contact-content">
          <div className="contact-form-section">
            <h2>Cuéntanos sobre tu evento</h2>

            {submitStatus === 'success' && (
              <div className="success-message">
                <i className="fas fa-check-circle"></i>
                <h3>¡Mensaje enviado correctamente!</h3>
                <p>
                  Te responderemos por correo en menos de 24 horas. También te
                  enviamos los datos por WhatsApp como respaldo.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                <h3>Oops, algo salió mal</h3>
                <p>
                  No pudimos enviar tu mensaje. Por favor, inténtalo de nuevo o
                  contáctanos directamente por WhatsApp.
                </p>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Tu nombre"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Tu correo electrónico"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Tu teléfono"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="Cuéntanos sobre tu evento: fecha, tipo de celebración, número de invitados..."
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <div className="form-group captcha-group">
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={RECAPTCHA_SITEKEY}
                  onChange={handleCaptcha}
                />
              </div>

              <button
                type="submit"
                className={`btn btn-primary btn-glow ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Enviando...
                  </>
                ) : (
                  '¡Enviar mensaje!'
                )}
              </button>
            </form>
          </div>

          <div className="contact-info-section">
            <h2>Otras formas de contactarnos</h2>
            <div className="contact-methods">
              <div className="contact-method">
                <div className="method-icon">
                  <i className="fab fa-whatsapp"></i>
                </div>
                <div className="method-info">
                  <h3>WhatsApp</h3>
                  <p>¡La forma más rápida!</p>
                  <a
                    href="https://wa.me/525522632166"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    +52 55 2263 2166
                  </a>
                </div>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="method-info">
                  <h3>Correo</h3>
                  <p>Te respondemos en menos de 24 horas</p>
                  <a href="mailto:ventas@procam360.mx">ventas@procam360.mx</a>
                </div>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <i className="fab fa-instagram"></i>
                </div>
                <div className="method-info">
                  <h3>Redes Sociales</h3>
                  <p>Síguenos para ver más contenido</p>
                  <div className="social-links">
                    <a
                      href="https://www.facebook.com/procam360mx"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook de ProCam360"
                    >
                      <svg viewBox="0 0 320 512" width="1em" height="1em" fill="currentColor">
                        <path d="M80 299.3V512H196V299.3h86.5l18-97.8H196V166.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4 .4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z"/>
                      </svg>
                      Facebook
                    </a>
                    <a
                      href="https://instagram.com/procam360mx"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram de ProCam360"
                    >
                      <svg viewBox="0 0 448 512" width="1em" height="1em" fill="currentColor">
                        <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
                      </svg>
                      Instagram
                    </a>
                    <a
                      href="https://tiktok.com/@procam360mx"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="TikTok de ProCam360"
                    >
                      <svg viewBox="0 0 448 512" width="1em" height="1em" fill="currentColor">
                        <path d="M448 209.9a210.1 210.1 0 0 1 -122.8-39.3V349.4A162.6 162.6 0 1 1 185 188.3V278.2a74.6 74.6 0 1 0 52.2 71.2V0l88 0a121.2 121.2 0 0 0 1.9 22.2h0A122.2 122.2 0 0 0 381 102.4a121.4 121.4 0 0 0 67 20.1z"/>
                      </svg>
                      TikTok
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
